import { BaseButton } from '@/src/components/buttons/BaseButton'
import { DestructiveButton } from '@/src/components/buttons/DestructiveButton'
import { SaveButton } from '@/src/components/buttons/SaveButton'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import {
  MEASURE_DETAIL_TAB,
  MEASURE_DETAIL_TAB_QUERY_PARAM,
} from '@/src/features/iso-suite/measures/mesaure-detail/measure-detail-tabs.constants'
import { useBaseToast } from '@/src/hooks/useBaseToast'
import { MEASURES_BASE_ROUTE, TABS } from '@/src/utils/constants'
import { Divider, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay } from '@chakra-ui/react'
import { useState } from 'react'
import { ExternalLink, PlusCircle } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSyncMeasureSimulationAsAdjustment } from '../../../details/hooks/useMeasureSimulations.hook'
import { mapToEnergyTypeDto } from '../../../details/types/adjustment.utils'
import { useLoadPlanningAdjustments, useLoadedStrategyMetadata } from '../../hooks/LoadPlanning.hooks'
import type { LoadPlanningMeasureModalItem } from './LoadPlanningMeasureModal.types'
import { LoadPlanningMeasureModalInfoSection } from './LoadPlanningMeasureModalInfoSection'
import { LoadPlanningMeasureModalSummaryTable } from './LoadPlanningMeasureModalSummaryTable'

interface LoadPlanningMeasureModal {
  isOpen: boolean
  onClose: () => void
  onDelete?: () => void
  isCandidate?: boolean
  measureItem: LoadPlanningMeasureModalItem
}

const LoadPlanningMeasureModal = ({ isOpen, onClose, onDelete, isCandidate, measureItem }: LoadPlanningMeasureModal) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentLocation, companyPgId: companyId } = useLocationsContext()
  const toast = useBaseToast()
  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const loadPlanningYear = strategyMetadata?.year

  const loadPlanningEnergyType = strategyMetadata?.energyType
  const locationId = currentLocation?.id ? Number(currentLocation.id) : undefined

  const { refetch: refetchAdjustments } = useLoadPlanningAdjustments({
    companyId,
    locationId,
    energyType: mapToEnergyTypeDto(loadPlanningEnergyType),
    year: loadPlanningYear ? Number(loadPlanningYear) : undefined,
  })

  const { mutateAsync: syncMeasureSimulationAsAdjustment, isPending: isSaving } = useSyncMeasureSimulationAsAdjustment()
  const [isRefetchingAdjustments, setIsRefetchingAdjustments] = useState(false)

  const navigateToMeasure = () => {
    if (!measureItem?.measureId) return
    navigate(
      `${MEASURES_BASE_ROUTE}/${TABS.measureHub.measures}/${measureItem.measureId}?${MEASURE_DETAIL_TAB_QUERY_PARAM}=${MEASURE_DETAIL_TAB.savingsPotential}`
    )
  }

  const simulationWasUpdated = measureItem.simulationChangeStatus === 'UPDATED'

  const syncSimulationToAdjustments = async () => {
    if (!measureItem?.id || !loadPlanningYear || !companyId) {
      toast({ status: 'error', title: t('components.procurement.forecasting.modal.form.error_notice') })
      return
    }

    try {
      await syncMeasureSimulationAsAdjustment({
        measureSimulationId: Number(measureItem?.id),
        requestBody: { companyId, year: loadPlanningYear.toString() },
      })
      setIsRefetchingAdjustments(true)
      await refetchAdjustments()
      onClose()
      toast({ status: 'success', title: t('components.procurement.forecasting.modal.form.success_notice') })
    } catch {
      toast({ status: 'error', title: t('components.procurement.forecasting.modal.form.error_notice') })
    } finally {
      setIsRefetchingAdjustments(false)
    }
  }

  return (
    <Modal size='2xl' motionPreset='slideInBottom' isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />

      <ModalContent>
        <ModalHeader pb={'0px'}>
          {isCandidate
            ? t('components.procurement.forecasting.measure_modal.header_candidate', { name: measureItem?.name })
            : t('components.procurement.forecasting.measure_modal.header_adjustment', { name: measureItem?.name })}
        </ModalHeader>
        <ModalCloseButton _focus={{}} />

        <ModalBody>
          <Flex direction='column' gap={4}>
            <LoadPlanningMeasureModalInfoSection measureItem={measureItem} isCandidate={isCandidate} />
            <LoadPlanningMeasureModalSummaryTable measureItem={measureItem} isCandidate={isCandidate} />
          </Flex>
        </ModalBody>

        <Divider height='1px' mt={'10px'} />

        <ModalFooter justifyContent={isCandidate ? 'end' : 'space-between'}>
          {!isCandidate && onDelete && (
            <DestructiveButton text={t('components.procurement.forecasting.modal.form.delete_button_text')} onClick={onDelete} />
          )}
          <Flex gap='10px'>
            <BaseButton
              variant='secondary'
              iconLeft={<ExternalLink size={16} />}
              text={t('components.procurement.forecasting.measure_modal.buttons.view_measure')}
              onClick={navigateToMeasure}
              isDisabled={!measureItem?.measureId}
            />

            {isCandidate && (
              <SaveButton
                leftIcon={<PlusCircle size={16} />}
                text={t('components.procurement.forecasting.measure_modal.buttons.add')}
                onClick={syncSimulationToAdjustments}
                isDisabled={isSaving || isRefetchingAdjustments}
                isLoading={isSaving || isRefetchingAdjustments}
              />
            )}
            {!isCandidate && simulationWasUpdated && (
              <SaveButton
                leftIcon={<PlusCircle size={16} />}
                text={t('components.procurement.forecasting.measure_modal.buttons.update')}
                onClick={syncSimulationToAdjustments}
                isDisabled={isSaving || isRefetchingAdjustments}
                isLoading={isSaving || isRefetchingAdjustments}
              />
            )}
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default LoadPlanningMeasureModal
